import { forwardRef } from 'react';
import { Product } from '@/types/store';
import { Link } from 'react-router-dom';
import { useCountryStore } from '@/store/countryStore';
import { ArrowUpLeft } from 'lucide-react';
import { getOptimizedStorageImage } from '@/lib/imageOptimization';

interface ProductCardProps {
  product: Product;
  index?: number;
}

const ProductCard = forwardRef<HTMLAnchorElement, ProductCardProps>(({ product, index = 0 }, ref) => {
  const { getAdjustedPrice, formatPrice } = useCountryStore();
  const displayPrice = getAdjustedPrice(product.price);
  const isFirstRow = index < 2;
  const image320 = getOptimizedStorageImage(product.image, 320, 62);
  const image480 = getOptimizedStorageImage(product.image, 480, 65);
  const image640 = getOptimizedStorageImage(product.image, 640, 68);

  return (
    <Link 
      ref={ref}
      to={`/product/${product.id}`}
      className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4"
    >
      <article className="overflow-hidden">
        <div className="relative mb-3 aspect-[3/4] overflow-hidden rounded-[1.35rem] bg-muted shadow-sm sm:rounded-[1.6rem]">
          <img
            src={image480}
            srcSet={`${image320} 320w, ${image480} 480w, ${image640} 640w`}
            sizes="(max-width: 639px) 48vw, (max-width: 1023px) 31vw, 23vw"
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.045]"
            width="480"
            height="640"
            loading={isFirstRow ? 'eager' : 'lazy'}
            decoding="async"
            fetchPriority={isFirstRow ? 'low' : 'auto'}
          />
          
          {!product.inStock && (
            <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
              <span className="text-foreground text-sm font-medium">نفذت الكمية</span>
            </div>
          )}
          <span className="absolute left-3 top-3 flex h-9 w-9 translate-y-1 items-center justify-center rounded-full bg-card/90 text-foreground opacity-0 shadow-md backdrop-blur-md transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
            <ArrowUpLeft className="h-4 w-4" />
          </span>
        </div>
        
        <div className="space-y-1.5 px-1">
          <h3 className="line-clamp-1 text-sm font-bold text-foreground sm:text-[15px]">
            {product.name}
          </h3>
          <p className="text-sm font-black text-foreground">
            {formatPrice(displayPrice)}
          </p>
        </div>
      </article>
    </Link>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
